import { useMemo, useState } from 'react';
import {
  Box, Paper, Typography, Table, TableHead, TableBody, TableRow, TableCell,
  TextField, InputAdornment, Tooltip,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import { buildBoard, formatScore } from './leaderboard';

const MEDALS = { 1: '#d8b25e', 2: '#b8b3aa', 3: '#b9825d' };
const SERIES = ['#d27a5a', '#c9b38a', '#7fa3a0', '#8f9bb8', '#b08aa8', '#9cae7c', '#c48f6b', '#8a857e'];
const num = { fontVariantNumeric: 'tabular-nums' };

function Stat({ label, value, hint }) {
  return (
    <Paper sx={{ px: 1.75, py: 1.25, borderRadius: 1.5, border: '1px solid', borderColor: 'divider', minWidth: 0 }}>
      <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: '0.06em', fontSize: '0.65rem' }}>
        {label}
      </Typography>
      <Typography sx={{ fontSize: '1.15rem', fontWeight: 600, lineHeight: 1.3, ...num }} noWrap>{value}</Typography>
      {hint && <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block' }}>{hint}</Typography>}
    </Paper>
  );
}

function Breakdown({ field, values, total }) {
  return (
    <Paper sx={{ px: 1.75, py: 1.25, borderRadius: 1.5, border: '1px solid', borderColor: 'divider' }}>
      <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: '0.06em', fontSize: '0.65rem' }}>
        By {field}
      </Typography>
      <Box sx={{ display: 'flex', height: 6, borderRadius: 3, overflow: 'hidden', my: 0.75, gap: '2px' }}>
        {values.map(([v, n], i) => (
          <Tooltip key={v} title={`${field} ${v}: ${n}`} arrow>
            <Box sx={{ flex: n, bgcolor: SERIES[i % SERIES.length] }} />
          </Tooltip>
        ))}
      </Box>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', columnGap: 1.5, rowGap: 0.25 }}>
        {values.map(([v, n], i) => (
          <Box key={v} sx={{ display: 'flex', alignItems: 'center', gap: 0.5, fontSize: '0.75rem', ...num }}>
            <Box sx={{ width: 7, height: 7, borderRadius: '50%', bgcolor: SERIES[i % SERIES.length] }} />
            <Box component="span" sx={{ color: 'text.primary', fontWeight: 600 }}>{v}</Box>
            <Box component="span" sx={{ color: 'text.secondary' }}>{n} · {Math.round((n / total) * 100)}%</Box>
          </Box>
        ))}
      </Box>
    </Paper>
  );
}

function RankBadge({ rank }) {
  const medal = MEDALS[rank];
  return (
    <Box sx={{
      width: 22, height: 22, borderRadius: '50%', display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
      fontSize: '0.7rem', fontWeight: 800, ...num,
      bgcolor: medal || 'transparent', color: medal ? '#16120f' : 'text.secondary',
    }}>
      {rank}
    </Box>
  );
}

export default function LeaderboardView({ data, mapping }) {
  const [query, setQuery] = useState('');
  const board = useMemo(() => buildBoard(data, mapping), [data, mapping]);
  const { rows, stats, breakdowns } = board;
  const fmt = (v) => formatScore(v, board.mapping?.format);

  if (board.error || !stats) {
    return (
      <Typography color="text.secondary" sx={{ p: 3 }}>
        {board.error || 'The source returned no rows with a numeric score.'}
      </Typography>
    );
  }

  const q = query.trim().toLowerCase();
  const visible = q ? rows.filter((r) => r.name.toLowerCase().includes(q)) : rows;
  const extra = board.mapping.extraFields || [];
  const lowerBetter = board.mapping.order === 'asc';

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 1.5 }}>
        <Stat label="Players" value={stats.count} />
        <Stat label="Leader" value={fmt(stats.best)} hint={stats.leader} />
        <Stat label="Median" value={fmt(stats.median)} hint={`+${fmt(Math.abs(stats.median - stats.best))} to #1`} />
        <Stat label="Average" value={fmt(stats.mean)} />
        <Stat label="Spread" value={fmt(stats.spread)} hint="#1 → last" />
      </Box>

      {breakdowns.length > 0 && (
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: 1.5 }}>
          {breakdowns.map((b) => <Breakdown key={b.field} {...b} total={stats.count} />)}
        </Box>
      )}

      <Paper sx={{ borderRadius: 1.5, border: '1px solid', borderColor: 'divider', overflow: 'hidden' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 1.75, py: 1, borderBottom: '1px solid', borderColor: 'divider' }}>
          <Typography variant="subtitle2" fontWeight={600} sx={{ flex: 1 }}>
            Standings
            <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>
              {lowerBetter ? 'lower is better' : 'higher is better'}
            </Typography>
          </Typography>
          {rows.length > 10 && (
            <TextField size="small" placeholder="Find player" value={query} onChange={(e) => setQuery(e.target.value)}
              sx={{ width: 180, '& .MuiInputBase-input': { py: 0.6, fontSize: '0.8rem' } }}
              InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon sx={{ fontSize: 16 }} /></InputAdornment> }}
            />
          )}
        </Box>
        <Box sx={{ overflowX: 'auto' }}>
          <Table size="small" sx={{
            '& td, & th': { borderColor: 'divider', py: 0.6, px: 1.25, whiteSpace: 'nowrap' },
            '& th': { fontSize: '0.68rem', color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '0.05em' },
          }}>
            <TableHead>
              <TableRow>
                <TableCell sx={{ width: 40 }}>#</TableCell>
                <TableCell>Player</TableCell>
                <TableCell align="right">Score</TableCell>
                <TableCell align="right">Gap</TableCell>
                <TableCell sx={{ width: '28%', minWidth: 120 }}>vs #1</TableCell>
                {extra.map((k) => <TableCell key={k} align="right">{k}</TableCell>)}
              </TableRow>
            </TableHead>
            <TableBody>
              {visible.map((r) => (
                <TableRow key={`${r.rank}-${r.name}`} hover>
                  <TableCell><RankBadge rank={r.rank} /></TableCell>
                  <TableCell sx={{ fontWeight: r.rank <= 3 ? 700 : 500, maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {r.name}
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600, ...num }}>{fmt(r.score)}</TableCell>
                  <TableCell align="right" sx={{ color: 'text.secondary', ...num }}>
                    {r.gap ? `+${fmt(r.gap)}` : '—'}
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Box sx={{ flex: 1, height: 5, borderRadius: 3, bgcolor: 'rgba(235,231,225,0.07)', overflow: 'hidden' }}>
                        <Box sx={{ width: `${Math.round(r.relative * 100)}%`, height: '100%', bgcolor: MEDALS[r.rank] || 'primary.main', opacity: MEDALS[r.rank] ? 0.9 : 0.55 }} />
                      </Box>
                      <Typography variant="caption" color="text.secondary" sx={{ width: 32, textAlign: 'right', ...num }}>
                        {Math.round(r.relative * 100)}%
                      </Typography>
                    </Box>
                  </TableCell>
                  {extra.map((k) => (
                    <TableCell key={k} align="right" sx={{ color: 'text.secondary', ...num }}>{String(r.extras[k] ?? '—')}</TableCell>
                  ))}
                </TableRow>
              ))}
              {visible.length === 0 && (
                <TableRow>
                  <TableCell colSpan={5 + extra.length} sx={{ color: 'text.secondary', textAlign: 'center', py: 2 }}>
                    No players match “{query}”
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </Box>
      </Paper>
    </Box>
  );
}
